import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CameraIcon, MaximizeIcon, MinimizeIcon, AlertTriangleIcon, ShieldCheckIcon, GridIcon, LayoutIcon } from 'lucide-react';
const cameras = [{
  id: 1,
  name: 'Main Lobby',
  location: 'Ground Floor',
  status: 'active',
  alert: false
}, {
  id: 2,
  name: 'Executive Floor',
  location: '15th Floor',
  status: 'active',
  alert: false
}, {
  id: 3,
  name: 'Parking Entrance',
  location: 'Basement',
  status: 'active',
  alert: true
}, {
  id: 4,
  name: 'Server Room',
  location: '3rd Floor',
  status: 'active',
  alert: false
}, {
  id: 5,
  name: 'Conference Hall',
  location: '10th Floor',
  status: 'active',
  alert: false
}, {
  id: 6,
  name: 'Reception',
  location: 'Ground Floor',
  status: 'active',
  alert: false
}];
export function CCTVView() {
  const [selectedCamera, setSelectedCamera] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'single'>('grid');
  const activeAlerts = cameras.filter(c => c.alert).length;
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} className="glass-card rounded-2xl p-6 h-full flex flex-col" data-id="element-39">
      {/* Header */}
      <div className="flex items-center justify-between mb-6" data-id="element-40">
        <div className="flex items-center gap-3" data-id="element-41">
          <div className="w-10 h-10 rounded-xl bg-[rgba(239,68,68,0.15)] flex items-center justify-center" data-id="element-42">
            <CameraIcon className="w-5 h-5 text-red-400" data-id="element-43" />
          </div>
          <div data-id="element-44">
            <h3 className="text-lg font-semibold font-['Playfair_Display'] text-white" data-id="element-45">
              Security Cameras
            </h3>
            <p className="text-xs text-gray-400" data-id="element-46">
              {cameras.length} cameras active
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3" data-id="element-47">
          {activeAlerts > 0 && <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[rgba(239,68,68,0.15)] border border-[rgba(239,68,68,0.3)]" data-id="element-48">
              <AlertTriangleIcon className="w-4 h-4 text-red-400" data-id="element-49" />
              <span className="text-sm text-red-400" data-id="element-50">{activeAlerts} Alert</span>
            </div>}
          <div className="flex items-center gap-1 bg-[rgba(255,255,255,0.05)] rounded-lg p-1" data-id="element-51">
            <button onClick={() => setViewMode('grid')} className={`p-2 rounded-md transition-colors ${viewMode === 'grid' ? 'bg-[rgba(201,168,76,0.2)] text-[#C9A84C]' : 'text-gray-400 hover:text-white'}`} data-id="element-52">
              <GridIcon className="w-4 h-4" data-id="element-53" />
            </button>
            <button onClick={() => setViewMode('single')} className={`p-2 rounded-md transition-colors ${viewMode === 'single' ? 'bg-[rgba(201,168,76,0.2)] text-[#C9A84C]' : 'text-gray-400 hover:text-white'}`} data-id="element-54">
              <LayoutIcon className="w-4 h-4" data-id="element-55" />
            </button>
          </div>
        </div>
      </div>

      {/* Camera Grid */}
      <div className={`flex-1 ${viewMode === 'grid' ? 'grid grid-cols-3 gap-3' : ''}`} data-id="element-56">
        {viewMode === 'grid' ? cameras.map((camera, index) => <motion.button key={camera.id} initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        delay: 0.05 * index
      }} onClick={() => setSelectedCamera(camera.id)} className={`relative rounded-xl overflow-hidden bg-[rgba(0,0,0,0.3)] aspect-video touch-button h-auto p-0 ${camera.alert ? 'ring-2 ring-red-500' : ''}`} data-id="element-57">
              {/* Simulated camera feed */}
              <div className="absolute inset-0 bg-gradient-to-br from-gray-800 to-gray-900" data-id="element-58">
                <img src={`https://images.unsplash.com/photo-1497366216548-37526070297c?w=300&h=200&fit=crop&q=60&sat=-100`} alt="" className="w-full h-full object-cover opacity-60" data-id="element-59" />
              </div>

              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" data-id="element-60" />

              {/* Recording indicator */}
              <div className="absolute top-2 right-2 flex items-center gap-1" data-id="element-61">
                <div className="w-3 h-3 text-red-500 animate-pulse" data-id="element-62" />
                <span className="text-xs text-red-400" data-id="element-63">REC</span>
              </div>

              {/* Alert badge */}
              {camera.alert && <div className="absolute top-2 left-2" data-id="element-64">
                  <AlertTriangleIcon className="w-5 h-5 text-red-500 animate-pulse" data-id="element-65" />
                </div>}

              {/* Camera info */}
              <div className="absolute bottom-0 left-0 right-0 p-2" data-id="element-66">
                <p className="text-xs font-medium text-white truncate" data-id="element-67">
                  {camera.name}
                </p>
                <p className="text-xs text-gray-400" data-id="element-68">{camera.location}</p>
              </div>

              {/* Expand button */}
              <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-69">
                <MaximizeIcon className="w-4 h-4 text-white" data-id="element-70" />
              </div>
            </motion.button>) : <div className="relative rounded-xl overflow-hidden bg-[rgba(0,0,0,0.3)] h-full" data-id="element-71">
            <img src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&h=600&fit=crop&q=80&sat=-100" alt="" className="w-full h-full object-cover opacity-70" data-id="element-72" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" data-id="element-73" />
            <div className="absolute top-4 right-4 flex items-center gap-2" data-id="element-74">
              <div className="w-4 h-4 text-red-500 animate-pulse" data-id="element-75" />
              <span className="text-sm text-red-400" data-id="element-76">LIVE</span>
            </div>
            <div className="absolute bottom-4 left-4" data-id="element-77">
              <p className="text-lg font-medium text-white" data-id="element-78">Main Lobby</p>
              <p className="text-sm text-gray-300" data-id="element-79">Ground Floor • Camera 1</p>
            </div>
          </div>}
      </div>

      {/* Footer */}
      <div className="mt-4 pt-4 border-t border-[rgba(201,168,76,0.1)] flex items-center justify-between" data-id="element-80">
        <div className="flex items-center gap-2 text-sm text-gray-400" data-id="element-81">
          <ShieldCheckIcon className="w-4 h-4 text-green-400" data-id="element-82" />
          <span data-id="element-83">All areas secured</span>
        </div>
        <button className="text-sm text-[#C9A84C] hover:text-[#D4AF37] transition-colors" data-id="element-84">
          Open Security Center
        </button>
      </div>
    </motion.div>;
}